import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button, NotificationModal } from '@components/ui';
import { useAssignDpoMutation } from '@dieterApi/dpo/useAssignDpoMutation';
import { useDpoInfoQuery } from '@dieterApi/dpo/useDpoInfoQuery';
import { FormControl, InputLabel, MenuItem, ModalProps, Select, SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends Omit<ModalProps, 'children'> {
  companyId: string;
  forced?: boolean;
}

export function DpoAssignmentModal({ companyId, forced = false, ...props }: Props) {
  const { t } = useTranslation();
  const { user } = useUserWithCompany();
  const { enqueueSnackbar } = useSnackbar();

  const { data: dpoInfoData } = useDpoInfoQuery(companyId);
  const [assignDpo, { loading }] = useAssignDpoMutation(companyId);

  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Get admin and DPO users for the company
  const company = user?.companies.find((c) => c.id === companyId);
  const adminUsers = company?.users?.filter((u) => u.role === 'Admin' || u.role === 'Dpo') || [];

  // Pre-select current DPO if exists
  useEffect(() => {
    if (dpoInfoData?.getDpoInfo?.userId) {
      setSelectedUserId(dpoInfoData.getDpoInfo.userId);
    }
  }, [dpoInfoData?.getDpoInfo?.userId]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserId(event.target.value);
  };

  const handleSubmit = () => {
    if (!selectedUserId) {
      enqueueSnackbar(t('dpo.modal.select_required', 'Bitte wählen Sie einen Benutzer aus'), { variant: 'warning' });
      return;
    }

    assignDpo({
      variables: {
        companyId,
        userId: selectedUserId,
      },
    })
      .then(() => {
        enqueueSnackbar(t('dpo.modal.success', 'Datenschutzbeauftragter wurde zugewiesen'), { variant: 'success' });
        props.onClose?.({}, 'backdropClick');
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  return (
    <NotificationModal {...props} withCloseButton={!forced}>
      <div className="flex flex-col gap-5 p-3 md:p-12 min-w-[400px]">
        <div className="h3">{t('dpo.modal.title', 'Datenschutzbeauftragten zuweisen')}</div>

        {forced && (
          <p className="text-gray-600 text-sm">
            {t(
              'dpo.modal.forced_description',
              'Bitte weisen Sie einen Datenschutzbeauftragten für dieses Unternehmen zu, um fortzufahren.'
            )}
          </p>
        )}

        <FormControl variant="filled" fullWidth>
          <InputLabel id="dpo-user-select-label">{t('dpo.modal.select_user', 'Benutzer auswählen')}</InputLabel>
          <Select
            labelId="dpo-user-select-label"
            id="dpo-user-select"
            value={selectedUserId}
            onChange={handleSelectChange}
            label={t('dpo.modal.select_user', 'Benutzer auswählen')}
          >
            {adminUsers.map((adminUser) => (
              <MenuItem key={adminUser.id} value={adminUser.id}>
                {adminUser.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {adminUsers.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            {t('dpo.modal.no_admins', 'Keine Admin-Benutzer für dieses Unternehmen gefunden.')}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <Button onClick={handleSubmit} disabled={loading || !selectedUserId} spinner={loading}>
              {t('common.save', 'Speichern')}
            </Button>
            {!forced && (
              <Button btnColor="gray" onClick={() => props.onClose?.({}, 'backdropClick')}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
            )}
          </div>
          {forced && (
            <button
              type="button"
              onClick={() => props.onClose?.({}, 'backdropClick')}
              className="text-sm text-gray-500 hover:text-gray-700 underline text-center"
            >
              {t('dpo.modal.do_later', 'Mache ich später...')}
            </button>
          )}
        </div>
      </div>
    </NotificationModal>
  );
}
